import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InsertAssessmentInput,
  InsertOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  GetAllOversightAssessmentsDocument,
  GetOversightAssessmentByIdDocument,
  InsertOversightAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type for insertOversightAssessment. The oversight_assessment
 * register is a near-1:1 twin of internal_audit_report (itself a twin of
 * assessment) and shares the InsertAssessmentInput wire shape, so the Status
 * field is the assessment status enum. Unlike internal_audit_report,
 * `Summary` is NOT NULL on oversight_assessment (schema.ts) and so is
 * required here.
 */
type InsertOversightAssessmentTRPCInput = {
  Title: string;
  Summary: string;
  Status: Assessment_Status_Enum;
  OriginatingItemId?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

export const useInsertOversightAssessment = createMutationHook({
  name: 'insertOversightAssessment',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.oversight.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (
    variables: InsertAssessmentInput
  ): InsertOversightAssessmentTRPCInput => ({
    ...variables,
    Title: variables.Title ?? '',
    Summary: variables.Summary ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
  }),
  invalidate: (trpc) => [
    trpc.frontend.oversight.register.queryKey(),
    trpc.frontend.oversight.oversightAssessmentCount.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertOversightAssessmentMutation => ({
    insertOversightAssessmentApi: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertOversightAssessmentDocument,
  graphqlVariables: (variables: InsertAssessmentInput) => ({
    object: variables,
  }),
  // The corresponding READS are NOT migrated and are cache-first, so a write
  // via tRPC still has to evict these or the Apollo-backed list/counter shows
  // pre-write data. Kept identical on both branches so they cannot drift.
  evictFields: ['oversight_assessment', 'oversight_assessment_aggregate'],
  refetchQueries: [
    GetOversightAssessmentByIdDocument,
    GetAllOversightAssessmentsDocument,
  ],
});
